import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Heading,
  Spinner,
  Center,
  Text,
  VStack,
  Grid,
  GridItem,
  Button,
  useToast,
} from "@chakra-ui/react";
import { secureFetch } from "../data/secureFetch";
const {
  formatDollarsToCents,
  formatCentsToDollars,
  formatDateTimeToLocalString,
} = require("../store/calc-helpers");

const RefundDonation = () => {
  const { donationId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [donation, setDonation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefunding, setIsRefunding] = useState(false);

  useEffect(() => {
    const fetchDonation = async () => {
      try {
        const response = await secureFetch(
          `/api/donations/detail/${donationId}`,
        );
        if (response.ok) {
          const data = response;
          setDonation({
            ...data,
            amount: formatCentsToDollars(data.donation_amount),
            original_fee: formatCentsToDollars(data.original_fee),
            actual_fee: formatCentsToDollars(data.actual_fee),
            platform_tip_amount: formatCentsToDollars(data.platform_tip_amount),
            actual_charge_amount: formatCentsToDollars(
              data.actual_charge_amount,
            ),
            refund_available: formatCentsToDollars(data.refund_available),
            profit_loss: formatCentsToDollars(data.profit_loss),
            created_at: formatDateTimeToLocalString(data.created_at),
            organization_name: data.organization.name,
            stripe_connected_account_id:
              data.organization.stripe_connected_account_id,
            stripeDetails: data.stripeDetails
              ? {
                  ...data.stripeDetails,
                  amount: formatCentsToDollars(data.stripeDetails.amount),
                  amount_capturable: formatCentsToDollars(
                    data.stripeDetails.amount_capturable,
                  ),
                  amount_received: formatCentsToDollars(
                    data.stripeDetails.amount_received,
                  ),
                  created: formatDateTimeToLocalString(
                    new Date(data.stripeDetails.created * 1000),
                  ),
                }
              : null,
          });
        } else {
          throw new Error("Failed to fetch donation details");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDonation();
  }, [donationId]);

  const handleRefund = async () => {
    setIsRefunding(true);
    try {
      const response = await secureFetch(`/api/refunds`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        data: {
          paymentId: donation.stripe_payment_intent_id,
          amount: formatDollarsToCents(donation.refund_available),
          platform_tip: formatDollarsToCents(donation.platform_tip_amount),
        },
      });

      if (response.ok) {
        const normalizedRefundStatus =
          `${response.refundStatus || ""}`.toLowerCase();
        const isImmediateSuccess = normalizedRefundStatus === "succeeded";
        toast({
          title: isImmediateSuccess ? "Refund Processed" : "Refund Requested",
          description: isImmediateSuccess
            ? "The refund was completed and the dashboard has been updated."
            : "The refund request was accepted. Status is now Refund Pending until Stripe confirms.",
          status: "success",
          duration: 9000,
          isClosable: true,
        });
        navigate(`/donations/${donationId}`);
      } else {
        throw new Error(response.message || "Failed to process refund");
      }
    } catch (err) {
      toast({
        title: "Refund Failed",
        description: err.message,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    } finally {
      setIsRefunding(false);
    }
  };

  if (loading) {
    return (
      <Center>
        <Spinner />
      </Center>
    );
  }

  if (error) {
    return <Box>Error: {error}</Box>;
  }

  if (!donation) {
    return <Box>No donation details available.</Box>;
  }

  return (
    <Box p={5} maxW="800px" mx="auto">
      <Heading mb={6}>Refund Donation</Heading>
      <Grid templateColumns="repeat(2, 1fr)" gap={4}>
        <GridItem>
          <Text>
            <strong>ID:</strong> {donation.id}
          </Text>
          <Text>
            <strong>Amount:</strong> ${donation.amount}
          </Text>
          <Text>
            <strong>Email:</strong> {donation.email}
          </Text>
          <Text>
            <strong>Name:</strong> {donation.first_name} {donation.last_name}
          </Text>
          <Text>
            <strong>Date:</strong> {donation.created_at}
          </Text>
        </GridItem>
      </Grid>
      <VStack spacing={4}>
        <Button
          colorScheme="red"
          onClick={handleRefund}
          isLoading={isRefunding}
          loadingText="Processing Refund"
        >
          Process Available Refund
        </Button>
      </VStack>
    </Box>
  );
};

export default RefundDonation;
